"use client";

import { useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Command as CommandPrimitive } from "cmdk";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { useCommandPalette } from "./command-palette-context";
import { SearchPage } from "./pages/SearchPage";
import { ScopePage } from "./pages/ScopePage";
import { EntityPage } from "./pages/EntityPage";
import { ConfirmPage, VerbConfirmPage } from "./pages/ConfirmPage";
import type { PaletteCtx } from "./types";

export function GlobalCommandPalette() {
  const router = useRouter();
  const pathname = usePathname();
  const { isOpen, close, pages, push, pop, reset } = useCommandPalette();
  const top = pages[pages.length - 1];

  const ctx = useMemo<PaletteCtx>(
    () => ({
      router: {
        push: (h) => router.push(h),
        replace: (h) => router.replace(h),
        back: () => router.back(),
      },
      pathname: pathname ?? "/",
      close,
      push,
      pop,
      reset,
    }),
    [router, pathname, close, push, pop, reset]
  );

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent
        showCloseButton={false}
        className="h-[min(620px,80vh)] w-[94vw] gap-0 overflow-hidden rounded-lg p-0 sm:max-w-2xl md:max-w-3xl"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Command Palette</DialogTitle>
          <DialogDescription>Search, navigate, and run commands</DialogDescription>
        </DialogHeader>

        <CommandPrimitive
          shouldFilter={false}
          loop
          /* Matches canonical shadcn `<Command>` shape minus bg-popover
             (the dialog is the surface — rung four of the elevation ramp).
             overflow-hidden is REQUIRED — without it CommandList's
             flex-1+overflow-y-auto can't engage; the cmdk root grows
             past the dialog and dialog's own overflow-hidden clips the
             bottom, producing the "can't scroll, content cut off" bug. */
          className="flex h-full w-full flex-col overflow-hidden text-popover-foreground"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              if (pages.length > 1) pop();
              else close();
              return;
            }
            if (e.key === "Backspace") {
              const inputEl = (e.target as HTMLElement | null)?.matches?.('[data-palette-input="true"]')
                ? (e.target as HTMLInputElement)
                : null;
              if (inputEl && inputEl.value === "" && pages.length > 1) {
                e.preventDefault();
                pop();
              }
            }
          }}
        >
          {top.kind === "search" && <SearchPage ctx={ctx} />}
          {top.kind === "scope" && <ScopePage ctx={ctx} scopeId={top.scopeId} />}
          {top.kind === "entity" && <EntityPage ctx={ctx} result={top.result} />}
          {top.kind === "confirm" && <ConfirmPage ctx={ctx} command={top.command} />}
          {top.kind === "verb-confirm" && (
            <VerbConfirmPage ctx={ctx} verb={top.verb} result={top.result} />
          )}
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}
